import { supabase } from '@/integrations/supabase/client';

export interface VenueAnalytics {
  activeVenues: number;
  todaysBookings: number;
  monthlyRevenue: number;
  todaysSchedule: Array<{
    id: string;
    field: string;
    teams: string;
    time: string;
    type: 'game' | 'event';
  }>;
  venueUtilization: Array<{
    name: string;
    utilization: number;
    status?: string;
  }>;
  customerAnalytics: {
    regularCustomers: number;
    satisfactionRate: number;
    averageRating: number;
  };
}

export const venueAnalyticsService = {
  async getVenueAnalytics(venueId: string): Promise<VenueAnalytics> {
    try {
      // Count claimed venues
      const { count: claimedVenues } = await supabase
        .from('venues')
        .select('*', { count: 'exact', head: true })
        .eq('claimed_by', venueId)
        .eq('is_active', true);

      // Count venue sections owned by user (indicating they operate venues)
      const { count: venueSections } = await supabase
        .from('venue_sections')
        .select('venue_id', { count: 'exact', head: true })
        .eq('venue_id', venueId)
        .eq('is_active', true);

      const activeVenues = (claimedVenues || 0) + (venueSections && venueSections > 0 ? 1 : 0);

      // Get today's bookings
      const today = new Date().toISOString().split('T')[0];
      const { count: todaysBookings } = await supabase
        .from('venue_games')
        .select('*', { count: 'exact', head: true })
        .eq('venue_id', venueId)
        .eq('game_date', today);

      // Calculate monthly revenue based on actual bookings and rates
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      
      // Get venue rates
      const { data: venueRates } = await supabase
        .from('venue_rates')
        .select('*')
        .eq('venue_id', venueId);

      // Get monthly bookings with duration
      const { data: monthlyBookings } = await supabase
        .from('venue_bookings')
        .select('*')
        .eq('venue_id', venueId)
        .eq('status', 'confirmed')
        .gte('created_at', startOfMonth.toISOString());

      // Calculate revenue based on actual rates and duration
      let monthlyRevenue = 0;
      if (monthlyBookings && venueRates && venueRates.length > 0) {
        monthlyRevenue = monthlyBookings.reduce((total, booking) => {
          const rate = venueRates[0]; // Use first rate as default
          const hours = (new Date(`2000-01-01 ${booking.end_time}`).getTime() - 
                        new Date(`2000-01-01 ${booking.start_time}`).getTime()) / (1000 * 60 * 60);
          return total + (hours * parseFloat(rate.hourly_rate.toString()));
        }, 0);
      } else {
        // Fallback calculation if no rates set
        const monthlyGames = await supabase
          .from('venue_games')
          .select('booking_duration_hours')
          .eq('venue_id', venueId)
          .eq('status', 'approved')
          .gte('created_at', startOfMonth.toISOString());
        
        const monthlyEvents = await supabase
          .from('venue_events')
          .select('booking_duration_hours')
          .eq('venue_id', venueId)
          .eq('status', 'approved')
          .gte('created_at', startOfMonth.toISOString());
        
        // Estimate: $50/hour default rate
        const gameHours = monthlyGames.data?.reduce((total, game) => total + (game.booking_duration_hours || 2), 0) || 0;
        const eventHours = monthlyEvents.data?.reduce((total, event) => total + (event.booking_duration_hours || 3), 0) || 0;
        monthlyRevenue = (gameHours + eventHours) * 50;
      }

      // Get today's schedule
      const { data: gamesData } = await supabase
        .from('venue_games')
        .select(`
          id,
          game_title,
          game_time,
          home_team_id,
          away_team_id
        `)
        .eq('venue_id', venueId)
        .eq('game_date', today)
        .order('game_time');

      // Get team names for games
      const gameSchedule = await Promise.all((gamesData || []).map(async (game) => {
        let homeTeamName = 'TBD';
        let awayTeamName = 'TBD';

        if (game.home_team_id) {
          const { data: homeTeam } = await supabase
            .from('teams')
            .select('team_name')
            .eq('team_id', game.home_team_id)
            .single();
          homeTeamName = homeTeam?.team_name || 'TBD';
        }

        if (game.away_team_id) {
          const { data: awayTeam } = await supabase
            .from('teams')
            .select('team_name')
            .eq('team_id', game.away_team_id)
            .single();
          awayTeamName = awayTeam?.team_name || 'TBD';
        }

        return {
          id: game.id,
          field: game.game_title || 'Field A',
          teams: `${homeTeamName} vs ${awayTeamName}`,
          time: game.game_time,
          type: 'game' as const
        };
      }));

      const { data: eventsData } = await supabase
        .from('venue_events')
        .select('id, event_title, event_time')
        .eq('venue_id', venueId)
        .eq('event_date', today)
        .order('event_time');

      const eventSchedule = (eventsData || []).map(event => ({
        id: event.id,
        field: event.event_title || 'Event',
        teams: event.event_title,
        time: event.event_time,
        type: 'event' as const
      }));

      const todaysSchedule = [...gameSchedule, ...eventSchedule]
        .sort((a, b) => a.time.localeCompare(b.time));

      // Calculate venue utilization based on actual operating hours
      const { data: venueProfile } = await supabase
        .from('profiles')
        .select('venue_name, venue_address, operating_hours_start, operating_hours_end, operating_days')
        .eq('id', venueId)
        .single();
      
      const venueName = venueProfile?.venue_name || 'Your Venue';
      
      // Calculate utilization based on operating hours
      let currentUtilization = 0;
      if (venueProfile?.operating_hours_start && venueProfile?.operating_hours_end) {
        const startHour = parseInt(venueProfile.operating_hours_start.split(':')[0]);
        const endHour = parseInt(venueProfile.operating_hours_end.split(':')[0]);
        const dailyHours = endHour - startHour;
        const possibleHours = dailyHours > 0 ? dailyHours : 16; // Default 16 hours if invalid
        
        // Get today's actual bookings duration
        const { data: todaysBookingHours } = await supabase
          .from('venue_bookings')
          .select('start_time, end_time')
          .eq('venue_id', venueId)
          .eq('booking_date', today);
        
        const bookedHours = todaysBookingHours?.reduce((total, booking) => {
          const hours = (new Date(`2000-01-01 ${booking.end_time}`).getTime() - 
                        new Date(`2000-01-01 ${booking.start_time}`).getTime()) / (1000 * 60 * 60);
          return total + hours;
        }, 0) || 0;
        
        currentUtilization = Math.min(100, Math.round((bookedHours / possibleHours) * 100));
      } else {
        // Fallback calculation
        const totalPossibleSlots = 16;
        currentUtilization = Math.min(100, Math.round(((todaysBookings || 0) / totalPossibleSlots) * 100));
      }
      
      const venueUtilization = [
        { 
          name: venueName, 
          utilization: currentUtilization
        }
      ];

      // Calculate customer analytics based on real feedback data
      const { data: customerFeedback } = await supabase
        .from('customer_feedback')
        .select('rating, customer_id')
        .eq('venue_id', venueId);

      // Get unique customers who have booked
      const { data: uniqueCustomers } = await supabase
        .from('venue_bookings')
        .select('customer_id')
        .eq('venue_id', venueId)
        .eq('status', 'confirmed');

      const totalCustomers = new Set(uniqueCustomers?.map(b => b.customer_id) || []).size;
      
      // Count customers with multiple bookings (regular customers)
      const customerBookingCounts: Record<string, number> = {};
      uniqueCustomers?.forEach(booking => {
        customerBookingCounts[booking.customer_id] = (customerBookingCounts[booking.customer_id] || 0) + 1;
      });
      
      const regularCustomers = Object.values(customerBookingCounts).filter(count => count >= 3).length;
      
      // Calculate satisfaction from actual feedback
      const totalRatings = customerFeedback?.length || 0;
      const averageRating = totalRatings > 0 
        ? customerFeedback!.reduce((sum, feedback) => sum + feedback.rating, 0) / totalRatings
        : 0;
      
      const satisfactionRate = totalRatings > 0
        ? Math.round((customerFeedback!.filter(f => f.rating >= 4).length / totalRatings) * 100)
        : 0;

      const customerAnalytics = {
        regularCustomers,
        satisfactionRate,
        averageRating: Math.round(averageRating * 10) / 10
      };

      return {
        activeVenues,
        todaysBookings: todaysBookings || 0,
        monthlyRevenue,
        todaysSchedule,
        venueUtilization,
        customerAnalytics
      };
    } catch (error) {
      console.error('Error fetching venue analytics:', error);
      return {
        activeVenues: 0,
        todaysBookings: 0,
        monthlyRevenue: 0,
        todaysSchedule: [],
        venueUtilization: [],
        customerAnalytics: {
          regularCustomers: 0,
          satisfactionRate: 0,
          averageRating: 0
        }
      };
    }
  }
};